import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Clock, ArrowLeft, Heart, MessageCircle, Play, Pause, Download, Share2, Eye, Calendar, MapPin, Globe, Instagram, Filter, RefreshCw, Music, ChevronDown } from 'lucide-react';
import AccessLevelBadge from '../components/AccessLevelBadge';
import { getAccessLabel, isGated } from '../utils/accessLabels';
import { profileThumb, profileThumbFallback, optimizedCover } from '../utils/imageHelpers';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import ShareModal from '../components/ShareModal';
import DownloadPaywall from '../components/DownloadPaywall';
import SignupInviteModal from '../components/SignupInviteModal';
import AudioPlayer from '../components/AudioPlayer';
import AccessRequiredModal from '../components/AccessRequiredModal';
import trackingService from '../services/tracking';

const ArtistFeed = () => {
    const { username, filter: urlFilter } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, hasActiveSubscription } = useAuth();
    const [artist, setArtist] = useState(null);
    const [posts, setPosts] = useState([]);
    const [allPosts, setAllPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState(() => {
        // Initialize filter from URL parameter
        const urlToFilter = {
            'posts': 'post',
            'audio': 'audio',
            'video': 'video',
            'videos': 'video',
            'images': 'image'
        };
        return urlToFilter[urlFilter] || 'all';
    });
    const [shareModal, setShareModal] = useState({ isOpen: false, item: null });
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, item: null });
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'content' });
    const [accessModal, setAccessModal] = useState({ isOpen: false, item: null });
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [playingVideoId, setPlayingVideoId] = useState(null);
    const videoRefs = useRef({});
    // Sort controls — clicking the active sort option again reverses direction
    // (e.g. Date starts "most recent first", click it again for "least recent first").
    // Initial value can come from a ?sort= query param (e.g. links from the
    // "Top Tracks"/"Top Artwork" sections land here pre-sorted by views).
    const [sortBy, setSortBy] = useState(() => {
        const urlSort = new URLSearchParams(location.search).get('sort');
        return ['date', 'views', 'likes'].includes(urlSort) ? urlSort : 'date';
    });
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' | 'asc'
    const [showSortOptions, setShowSortOptions] = useState(false);

    const handleSortClick = (criterion) => {
        if (sortBy === criterion) {
            setSortDirection(prev => (prev === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortBy(criterion);
            setSortDirection('desc');
        }
    };

    const sortedPosts = React.useMemo(() => {
        const getValue = (post) => {
            switch (sortBy) {
                case 'views': return post.play_count || 0;
                case 'likes': return post.like_count || 0;
                case 'date':
                default: return new Date(post.created_at).getTime();
            }
        };
        const sorted = [...posts].sort((a, b) => getValue(b) - getValue(a)); // desc baseline
        return sortDirection === 'asc' ? sorted.reverse() : sorted;
    }, [posts, sortBy, sortDirection]);

    const handleVideoPlayPause = (e, postId) => {
        e.stopPropagation();
        const videoEl = videoRefs.current[postId];
        if (!videoEl) return;
        if (playingVideoId === postId) {
            videoEl.pause();
            trackingService.stopPlayTracking(postId);
            setPlayingVideoId(null);
        } else {
            if (playingVideoId && videoRefs.current[playingVideoId]) {
                videoRefs.current[playingVideoId].pause();
                trackingService.stopPlayTracking(playingVideoId);
            }
            setPlayingVideoId(postId);
            videoEl.play();
            trackingService.startPlayTracking(postId, videoEl);
        }
    };

    const toggleExpanded = (key, e) => {
        e.stopPropagation();
        setExpandedItems(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const needsExpansion = (text) =>
        text && (text.split('\n').length > 8 || text.length > 500);

    useEffect(() => {
        fetchArtistData();
        fetchArtistPosts();
    }, [username]);

    useEffect(() => {
        applyFilter();
    }, [filter, allPosts]);

    // Handle URL parameter changes (back/forward navigation)
    useEffect(() => {
        const urlToFilter = {
            'posts': 'post',
            'audio': 'audio',
            'video': 'video',
            'videos': 'video',
            'images': 'image'
        };
        const newFilter = urlToFilter[urlFilter] || 'all';
        
        if (newFilter !== filter) {
            setFilter(newFilter);
        }
    }, [urlFilter]);

    // Update page metadata
    useEffect(() => {
        if (artist) {
            const artistName = artist.displayName || artist.username;
            
            const filterMetadata = {
                all: {
                    title: `${artistName} - Feed`,
                    description: `Latest posts and media from ${artistName} on The Deep Wave. Discover their music, videos, artwork, and posts all in one place.`,
                    keywords: `${artistName}, the deep wave, artist feed, music, videos, artwork, posts, ${artist.username}`,
                    ogDescription: `Explore all content from ${artistName} on The Deep Wave`
                },
                post: {
                    title: `${artistName} - Posts`,
                    description: `Latest text posts and discussions from ${artistName}. Read their thoughts, updates, and connect with this talented artist on The Deep Wave.`,
                    keywords: `${artistName}, posts, discussions, updates, the deep wave, ${artist.username}`,
                    ogDescription: `Read the latest posts from ${artistName}`
                },
                audio: {
                    title: `${artistName} - Music`,
                    description: `Latest music and audio content from ${artistName}. Stream their tracks, demos, and musical creations on The Deep Wave.`,
                    keywords: `${artistName}, music, audio, tracks, songs, streaming, the deep wave, ${artist.username}`,
                    ogDescription: `Listen to music by ${artistName} on The Deep Wave`
                },
                video: {
                    title: `${artistName} - Videos`,
                    description: `Latest video content from ${artistName}. Watch their music videos, behind-the-scenes content, and creative videos on The Deep Wave.`,
                    keywords: `${artistName}, videos, music videos, content, visual, the deep wave, ${artist.username}`,
                    ogDescription: `Watch videos by ${artistName} on The Deep Wave`
                },
                image: {
                    title: `${artistName} - Artwork`,
                    description: `Latest artwork and visual creations from ${artistName}. Explore their digital art, photography, and creative visual content on The Deep Wave.`,
                    keywords: `${artistName}, artwork, visual art, digital art, photography, creative, the deep wave, ${artist.username}`,
                    ogDescription: `View artwork by ${artistName} on The Deep Wave`
                }
            };
            
            const metadata = filterMetadata[filter] || filterMetadata.all;
            
            // Update page title
            document.title = `${metadata.title} | The Deep Wave`;
            
            // Update meta description
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
                metaDescription.content = metadata.description;
            }

            // Update or create keywords meta tag
            updateMetaTag('keywords', metadata.keywords, 'name');

            // Update Open Graph tags
            updateMetaTag('og:title', `${metadata.title} | The Deep Wave`);
            updateMetaTag('og:description', metadata.ogDescription);
            updateMetaTag('og:url', window.location.href);
            updateMetaTag('og:type', 'profile');
            updateMetaTag('og:site_name', 'The Deep Wave');
            
            // Use artist profile image or fallback
            const ogImageUrl = artist.profilePictureUrl || 'https://thedeepwave.com/og-image.jpg';
            updateMetaTag('og:image', ogImageUrl);
            updateMetaTag('og:image:alt', `${artistName} - The Deep Wave Artist`);

            // Twitter Card tags
            updateMetaTag('twitter:card', 'summary_large_image', 'name');
            updateMetaTag('twitter:title', `${metadata.title} | The Deep Wave`, 'name');
            updateMetaTag('twitter:description', metadata.ogDescription, 'name');
            updateMetaTag('twitter:image', ogImageUrl, 'name');

            // Additional profile-specific meta tags
            updateMetaTag('profile:username', artist.username);
            if (artist.displayName) {
                updateMetaTag('profile:first_name', artist.displayName);
            }

            // Additional SEO tags
            updateMetaTag('robots', 'index, follow', 'name');
            updateMetaTag('author', artistName, 'name');
            updateMetaTag('viewport', 'width=device-width, initial-scale=1.0', 'name');
        }
    }, [artist, filter]);

    const updateMetaTag = (property, content, type = 'property') => {
        let selector = type === 'name' ? `meta[name="${property}"]` : `meta[property="${property}"]`;
        let tag = document.querySelector(selector);
        
        if (!tag) {
            tag = document.createElement('meta');
            if (type === 'name') {
                tag.name = property;
            } else {
                tag.property = property;
            }
            document.head.appendChild(tag);
        }
        tag.content = content;
    };

    const fetchArtistData = async () => {
        try {
            const response = await fetch(`/api/profile/${username}`);
            const result = await response.json();

            if (result.success) {
                setArtist(result.profile || result.data);
            } else {
                // Only a real 404 means the artist genuinely doesn't exist —
                // any other failure is transient and shouldn't claim that.
                setError(response.status === 404 ? 'not_found' : 'server_error');
            }
        } catch (err) {
            console.error('Error fetching artist:', err);
            setError('server_error');
        }
    };

    const fetchArtistPosts = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch all media types for this specific artist
            let songsRes, videosRes, imagesRes, postsRes;
            
            try {
                [songsRes, videosRes, imagesRes, postsRes] = await Promise.all([
                    api.get(`/upload/public/audio?username=${encodeURIComponent(username)}&limit=100`),
                    api.get(`/upload/public/video?username=${encodeURIComponent(username)}&limit=100`),
                    api.get(`/upload/public/image?username=${encodeURIComponent(username)}&limit=100`),
                    api.get(`/posts/profile/${encodeURIComponent(username)}?limit=100`)
                ]);
            } catch (fetchError) {
                console.error('Error in Promise.all:', fetchError);
                // Try fetching individually if Promise.all fails
                songsRes = await api.get(`/upload/public/audio?username=${encodeURIComponent(username)}&limit=100`).catch(e => ({ data: { success: false } }));
                videosRes = await api.get(`/upload/public/video?username=${encodeURIComponent(username)}&limit=100`).catch(e => ({ data: { success: false } }));
                imagesRes = await api.get(`/upload/public/image?username=${encodeURIComponent(username)}&limit=100`).catch(e => ({ data: { success: false } }));
                postsRes = await api.get(`/posts/profile/${encodeURIComponent(username)}?limit=100`).catch(e => ({ data: { success: false } }));
            }

            // Combine all posts with type indicators. The backend endpoints are already
            // scoped to this username (owned uploads + uploads/posts they're tagged as a
            // contributing artist on) — don't re-filter by username here, or tagged content
            // (which carries the original uploader's username) gets silently stripped back out.
            const artistPosts = [];

            if (songsRes.data.success && songsRes.data.data) {
                songsRes.data.data.forEach(song => {
                    artistPosts.push({ ...song, mediaType: 'audio' });
                });
            }

            if (videosRes.data.success && videosRes.data.data) {
                videosRes.data.data.forEach(video => {
                    artistPosts.push({ ...video, mediaType: 'video' });
                });
            }

            if (imagesRes.data.success && imagesRes.data.data) {
                imagesRes.data.data.forEach(image => {
                    artistPosts.push({ ...image, mediaType: 'image' });
                });
            }

            if (postsRes.data.success && postsRes.data.data) {
                // Fetch comments for posts
                const userPosts = postsRes.data.data;

                const postsWithComments = await Promise.all(
                    userPosts.map(async (post) => {
                        let comments = [];
                        try {
                            const commentsRes = await api.get(`/posts/${post.id}/comments?limit=3`);
                            if (commentsRes.data.success) {
                                comments = commentsRes.data.data;
                            }
                        } catch (err) {
                            console.error(`Error fetching comments for post ${post.id}:`, err);
                        }
                        return { ...post, mediaType: 'post', previewComments: comments };
                    })
                );
                postsWithComments.forEach(post => {
                    artistPosts.push(post);
                });
            }

            // Sort by creation date (most recent first)
            const sortedPosts = artistPosts.sort((a, b) => 
                new Date(b.created_at) - new Date(a.created_at)
            );

            setAllPosts(sortedPosts);
        } catch (err) {
            console.error('Error fetching artist posts:', err);
            setError(prev => prev || 'server_error');
        } finally {
            setLoading(false);
        }
    };

    const applyFilter = () => {
        if (filter === 'all') {
            setPosts(allPosts);
        } else {
            const filteredPosts = allPosts.filter(post => post.mediaType === filter);
            setPosts(filteredPosts);
        }
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        // Update URL with user-friendly names
        const filterToUrl = {
            'post': 'posts',
            'audio': 'audio',
            'video': 'videos', 
            'image': 'images'
        };
        
        const basePath = `/feed/artist/${username}`;
        const urlFilter = filterToUrl[newFilter];
        const newPath = newFilter === 'all' ? basePath : `${basePath}/${urlFilter}`;
        navigate(newPath, { replace: true });
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
    };

    const getMediaIcon = (mediaType) => {
        switch (mediaType) {
            case 'audio': return '🎵';
            case 'video': return '🎥';
            case 'image': return '🖼️';
            case 'post': return '💬';
            default: return '📄';
        }
    };

    const getMediaBorderClass = (mediaType) => {
        switch (mediaType) {
            case 'audio': return 'border-green-500/30 bg-green-500/5';
            case 'video': return 'border-red-500/30 bg-red-500/5';
            case 'image': return 'border-cyan-500/30 bg-cyan-500/5';
            case 'post': return 'border-purple-500/30 bg-purple-500/5';
            default: return 'border-gray-500/30 bg-gray-500/5';
        }
    };

    const handlePostClick = (post) => {
        if (post.mediaType === 'image') {
            const filename = post.stored_filename || post.original_filename || post.id;
            navigate(`/view/image/${post.username}/${filename}`);
        } else if (post.mediaType === 'video') {
            const filename = post.stored_filename || post.original_filename || post.id;
            navigate(`/view/video/${post.username}/${filename}`);
        } else if (post.mediaType === 'audio') {
            const filename = post.stored_filename || post.original_filename || post.id;
            navigate(`/view/audio/${post.username}/${filename}`);
        } else if (post.mediaType === 'post') {
            navigate(`/view/post/${post.author_username}/${post.id}`);
        }
    };

    const handleLike = async (post) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: post.mediaType || 'content' });
            return;
        }

        try {
            let response;
            if (post.mediaType === 'post') {
                response = await api.post(`/posts/${post.id}/like`);
            } else {
                response = await api.post(`/upload/${post.id}/like`);
            }
            
            if (response.data.success) {
                setPosts(prev => prev.map(p => 
                    p.id === post.id && p.mediaType === post.mediaType
                        ? { 
                            ...p, 
                            like_count: response.data.data.likeCount,
                            user_has_liked: response.data.data.liked 
                        }
                        : p
                ));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const handleShareClick = (item) => {
        setShareModal({ isOpen: true, item });
    };

    const handleDownloadClick = (item) => {
        setDownloadPaywall({ isOpen: true, item });
    };

    if (error === 'not_found') {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4">🎨</div>
                    <h1 className="text-2xl font-bold text-white mb-2">Artist Not Found</h1>
                    <p className="text-gray-400 mb-6">The artist you're looking for doesn't exist or has no public content.</p>
                    <Link
                        to="/feed"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Feed
                    </Link>
                </div>
            </div>
        );
    }

    if (error === 'server_error') {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4">⚠️</div>
                    <h1 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h1>
                    <p className="text-gray-400 mb-6">We had trouble loading this page. Please try again.</p>
                    <button
                        onClick={() => {
                            setError(null);
                            fetchArtistData();
                            fetchArtistPosts();
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            {artist && (
                <PageHeader 
                    title={`${artist.displayName || artist.username}'s Feed`}
                    subtitle={`${filter === 'all' ? 'All posts and media' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} content`} by ${artist.displayName || artist.username} • ${posts.length} items`}
                    icon={<Clock className="w-8 h-8" />}
                />
            )}

            {/* Artist Info Banner */}
            {artist && (
                <div className="relative overflow-hidden bg-gradient-to-br from-purple-900/40 via-blue-900/30 to-pink-900/40">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                        <div className="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left gap-6">
                            {/* Artist Avatar */}
                            <div className="flex-shrink-0">
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-4 border-white/20 shadow-xl">
                                    {artist.profilePictureUrl ? (
                                        <img
                                            src={artist.profilePictureUrl}
                                            alt={artist.displayName || artist.username}
                                            width="80"
                                            height="80"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                            <span className="text-white text-xl sm:text-2xl font-bold">
                                                {(artist.displayName || artist.username)?.charAt(0) || 'A'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Artist Info */}
                            <div className="flex-1 min-w-0 w-full flex flex-col items-center sm:items-stretch">
                                <div className="flex flex-col items-center sm:flex-row sm:items-start sm:justify-between gap-4 w-full">
                                    <div className="flex-1 min-w-0 flex flex-col items-center sm:items-start">
                                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                            {artist.displayName || artist.username}
                                        </h1>
                                        <p className="text-purple-200 mb-3 flex items-center gap-2">
                                            <span>🌊</span>
                                            @{artist.username}
                                        </p>

                                        {artist.bio && (
                                            <p className="text-gray-300 mb-3 max-w-2xl">
                                                {artist.bio}
                                            </p>
                                        )}

                                        {/* Stats */}
                                        <div className="flex items-center gap-4 mb-3">
                                            <div className="text-center">
                                                <div className="text-lg font-bold text-white">{posts.length}</div>
                                                <div className="text-xs text-gray-400">Posts</div>
                                            </div>
                                        </div>

                                        {/* Location */}
                                        {artist.location && (
                                            <p className="text-gray-400 text-sm flex items-center gap-2 mb-3">
                                                <MapPin className="w-4 h-4" />
                                                {artist.location}
                                            </p>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-row sm:flex-shrink-0 gap-3 w-full sm:w-auto">
                                        <Link
                                            to={`/profile/${artist.username}`}
                                            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                                        >
                                            <span>🌊</span>
                                            View Profile
                                        </Link>

                                        <Link
                                            to="/feed"
                                            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
                                        >
                                            <ArrowLeft className="w-4 h-4" />
                                            Back to Feed
                                        </Link>
                                    </div>
                                </div>

                                {/* Social Links */}
                                {(artist.website || artist.instagram) && (
                                    <div className="flex items-center justify-center sm:justify-start gap-3 mt-4 pt-4 border-t border-white/10 w-full">
                                        {artist.website && (
                                            <a
                                                href={artist.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm"
                                            >
                                                <Globe className="w-4 h-4" />
                                                Website
                                            </a>
                                        )}
                                        {artist.instagram && (
                                            <a
                                                href={`https://instagram.com/${artist.instagram.replace('@', '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-3 py-1 bg-pink-600/20 hover:bg-pink-600/30 text-pink-200 rounded-lg transition-colors text-sm"
                                            >
                                                <Instagram className="w-4 h-4" />
                                                Instagram
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                {/* Filter Controls */}
                <div className="mb-8 space-y-4 lg:space-y-0">
                    {/* Header Row */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Filter className="w-5 h-5 text-gray-400" />
                            <span className="text-gray-400 font-medium hidden sm:inline">Filter:</span>
                        </div>
                        <button
                            onClick={fetchArtistPosts}
                            disabled={loading}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
                            title="Refresh Feed"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                    </div>
                    
                    {/* Filter Buttons */}
                    {(() => {
                        const filterOptions = [
                            { id: 'all', label: 'All Media', icon: '📂' },
                            { id: 'post', label: 'Posts', icon: '💬' },
                            { id: 'audio', label: 'Music', icon: '🎵' },
                            { id: 'video', label: 'Videos', icon: '🎥' },
                            { id: 'image', label: 'Artwork', icon: '🖼️' }
                        ];
                        const activeOption = filterOptions.find(o => o.id === filter) || filterOptions[0];
                        return (
                            <div className="relative">
                                {/* Mobile: dropdown */}
                                <div className="sm:hidden">
                                    <button
                                        onClick={() => setShowFilterDropdown(v => !v)}
                                        className="flex items-center justify-between w-full gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg text-sm font-medium"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span>{activeOption.icon}</span>
                                            <span>{activeOption.label}</span>
                                        </span>
                                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showFilterDropdown ? 'rotate-180' : ''}`} />
                                    </button>
                                    {showFilterDropdown && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden z-20 shadow-xl">
                                            {filterOptions.map((filterOption) => (
                                                <button
                                                    key={filterOption.id}
                                                    onClick={() => { handleFilterChange(filterOption.id); setShowFilterDropdown(false); }}
                                                    className={`flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors ${
                                                        filter === filterOption.id
                                                            ? 'bg-blue-600/20 text-blue-400'
                                                            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                                    }`}
                                                >
                                                    <span>{filterOption.icon}</span>
                                                    <span>{filterOption.label}</span>
                                                    {filter === filterOption.id && <span className="ml-auto text-blue-400 text-xs">✓</span>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Desktop: horizontal scroll */}
                                <div className="hidden sm:block overflow-x-auto">
                                    <div className="flex gap-2 min-w-max pb-2">
                                        {filterOptions.map((filterOption) => (
                                            <button
                                                key={filterOption.id}
                                                onClick={() => handleFilterChange(filterOption.id)}
                                                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                                                    filter === filterOption.id
                                                        ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                                                        : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
                                                }`}
                                            >
                                                <span>{filterOption.icon}</span>
                                                <span>{filterOption.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Sort Controls */}
                    {(() => {
                        const sortOptions = [
                            { id: 'date', label: 'Date', icon: Clock },
                            { id: 'views', label: 'Views', icon: Eye },
                            { id: 'likes', label: 'Likes', icon: Heart },
                        ];
                        const activeSort = sortOptions.find(o => o.id === sortBy) || sortOptions[0];
                        return (
                            <div className="flex items-center gap-2 pt-4 border-t border-gray-800/50">
                                <button
                                    onClick={() => setShowSortOptions(v => !v)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                        showSortOptions
                                            ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                                            : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50 border border-transparent'
                                    }`}
                                >
                                    <activeSort.icon className="w-3.5 h-3.5" />
                                    <span>Sort by: {activeSort.label}</span>
                                    <span className="text-xs">{sortDirection === 'desc' ? '↓' : '↑'}</span>
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showSortOptions ? 'rotate-180' : ''}`} />
                                </button>

                                {showSortOptions && sortOptions.map(({ id, label, icon: Icon }) => {
                                    const active = sortBy === id;
                                    return (
                                        <button
                                            key={id}
                                            onClick={() => handleSortClick(id)}
                                            title={active ? `Sorted ${sortDirection === 'desc' ? 'newest/highest first — click to reverse' : 'oldest/lowest first — click to reverse'}` : `Sort by ${label}`}
                                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                                active
                                                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                                                    : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50 border border-transparent'
                                            }`}
                                        >
                                            <Icon className="w-3.5 h-3.5" />
                                            <span>{label}</span>
                                            {active && (
                                                <span className="text-xs">{sortDirection === 'desc' ? '↓' : '↑'}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-0 pb-8">
                {loading && (
                    <div className="space-y-4">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-6 border-y sm:border border-gray-700/50 animate-pulse">
                                <div className="h-4 bg-gray-700 rounded w-3/4 mb-4"></div>
                                <div className="h-32 bg-gray-700 rounded mb-4"></div>
                                <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && posts.length === 0 && (
                    <div className="text-center py-16">
                        <Clock className="w-24 h-24 text-gray-600 mx-auto mb-6" />
                        <h2 className="text-2xl font-bold text-white mb-2">No Posts Yet</h2>
                        <p className="text-gray-400 mb-6">
                            {artist?.displayName || artist?.username || 'This artist'} hasn't shared any content yet.
                        </p>
                        <Link
                            to="/feed"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                        >
                            Browse All Artists
                        </Link>
                    </div>
                )}

                {!loading && posts.length > 0 && (
                    <div className="space-y-4 sm:space-y-6">
                        {sortedPosts.map((post) => (
                            <div
                                key={`${post.mediaType}-${post.id}`}
                                className={`glass-card p-0 border-y sm:border -mx-4 sm:mx-0 rounded-none sm:rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 sm:hover:scale-[1.02] cursor-pointer ${getMediaBorderClass(post.mediaType)}`}
                                onClick={() => handlePostClick(post)}
                            >
                                {/* Post Header */}
                                <div className="flex items-start justify-between p-5 pb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                                            {artist?.profilePictureUrl ? (
                                                <img
                                                    src={profileThumb(artist.profilePictureUrl)}
                                                    alt={artist.displayName || artist.username}
                                                    width="32"
                                                    height="32"
                                                    className="w-full h-full object-cover"
                                                    onError={profileThumbFallback(artist.profilePictureUrl)}
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                                                    <span className="text-white text-xs">
                                                        {(artist?.displayName || artist?.username || post.username)?.charAt(0)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white flex items-center gap-2">
                                                {artist?.displayName || artist?.username || post.username || post.author_username}
                                                {(() => {
                                                    // The header always shows this feed's artist, but the item itself
                                                    // may actually belong to someone else who tagged them as a
                                                    // contributor — flag that instead of implying they posted it.
                                                    const ownerUsername = post.mediaType === 'post' ? post.author_username : post.username;
                                                    return ownerUsername && ownerUsername !== username ? (
                                                        <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                                            Contributor
                                                        </span>
                                                    ) : null;
                                                })()}
                                            </h3>
                                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatTimeAgo(post.created_at)}
                                                </span>
                                                <span>•</span>
                                                <span className="flex items-center gap-1">
                                                    <span>{getMediaIcon(post.mediaType)}</span>
                                                    {post.mediaType.charAt(0).toUpperCase() + post.mediaType.slice(1)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Text content */}
                                {(post.mediaType !== 'post'
                                    ? (post.title || post.original_filename || post.description)
                                    : (post.content || post.description)) && (
                                    <div className="px-5 pb-3">
                                        {post.mediaType !== 'post' && (post.title || post.original_filename) && (
                                            <h4 className="text-lg font-medium text-white mb-2">
                                                {post.title || post.original_filename}
                                            </h4>
                                        )}

                                        {post.description && (
                                            <div className="mb-2">
                                                <p
                                                    className="text-gray-300 whitespace-pre-wrap leading-relaxed"
                                                    style={!expandedItems.has(`desc-${post.id}`) && needsExpansion(post.description) ? { display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}}
                                                >
                                                    {post.description}
                                                </p>
                                                {needsExpansion(post.description) && (
                                                    <button onClick={(e) => toggleExpanded(`desc-${post.id}`, e)} className="text-blue-400 hover:text-blue-300 text-sm mt-1">
                                                        {expandedItems.has(`desc-${post.id}`) ? 'Show less' : 'Read more'}
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {post.content && post.mediaType === 'post' && (
                                            <div className="mb-2">
                                                <p
                                                    className="text-gray-300 whitespace-pre-wrap leading-relaxed"
                                                    style={!expandedItems.has(`content-${post.id}`) && needsExpansion(post.content) ? { display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}}
                                                >
                                                    {post.content}
                                                </p>
                                                {needsExpansion(post.content) && (
                                                    <button onClick={(e) => toggleExpanded(`content-${post.id}`, e)} className="text-blue-400 hover:text-blue-300 text-sm mt-1">
                                                        {expandedItems.has(`content-${post.id}`) ? 'Show less' : 'Read more'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Post attachments — full width */}
                                {post.mediaType === 'post' && post.attachments && post.attachments.length > 0 && (
                                    <div className="w-full">
                                        <div className={`grid gap-0.5 ${post.attachments.slice(0, 2).length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                            {post.attachments.slice(0, 2).map((attachment, idx) => (
                                                <div key={attachment.id} className="relative">
                                                    <img
                                                        src={attachment.optimized_url || attachment.file_url}
                                                        alt={`Attachment ${idx + 1}`}
                                                        className={`w-full bg-gray-900 object-contain ${post.attachments.slice(0, 2).length === 1 ? 'max-h-[500px]' : 'aspect-square'}`}
                                                        loading="lazy"
                                                    />
                                                    {idx === 1 && post.attachments.length > 2 && (
                                                        <Link
                                                            to={`/view/post/${post.author_username}/${post.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="absolute inset-0 bg-black/60 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                                                        >
                                                            <div className="text-center">
                                                                <Eye className="w-5 h-5 mx-auto mb-1" />
                                                                <span className="text-sm font-medium">+{post.attachments.length - 2} more</span>
                                                            </div>
                                                        </Link>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        {post.attachments.length > 2 && (
                                            <div className="px-5 py-2 text-center">
                                                <Link
                                                    to={`/view/post/${post.author_username}/${post.id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="inline-flex items-center gap-2 px-4 py-1.5 text-sm text-blue-400 hover:text-blue-300 bg-blue-400/10 hover:bg-blue-400/20 rounded-lg transition-colors"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    View all {post.attachments.length} images
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Standalone image — full width */}
                                {post.file_url && post.mediaType === 'image' && (
                                    isGated(post.access_level, isAuthenticated, hasActiveSubscription) ? (
                                        <div className="relative overflow-hidden bg-gray-900">
                                            <img
                                                src={post.optimized_url || post.file_url}
                                                alt={post.title || post.original_filename}
                                                className="w-full max-h-[500px] object-contain pointer-events-none"
                                                loading="lazy"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                                <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(post.access_level, 'view')}</span>
                                            </div>
                                            <AccessLevelBadge accessLevel={post.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                        </div>
                                    ) : (
                                        <img
                                            src={post.optimized_url || post.file_url}
                                            alt={post.title || post.original_filename}
                                            className="w-full max-h-[500px] object-contain bg-gray-900"
                                            loading="lazy"
                                        />
                                    )
                                )}

                                {/* Video — full width */}
                                {post.file_url && post.mediaType === 'video' && (
                                    isGated(post.access_level, isAuthenticated, hasActiveSubscription) ? (
                                        <div className="relative overflow-hidden bg-gray-900">
                                            <video
                                                src={post.file_url}
                                                poster={optimizedCover(post.cover_image_url)}
                                                className="w-full max-h-[90vh] object-contain pointer-events-none"
                                                preload="metadata"
                                                playsInline
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                                <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(post.access_level, 'view')}</span>
                                            </div>
                                            <AccessLevelBadge accessLevel={post.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                        </div>
                                    ) : (
                                        <div className="relative bg-gray-900 group" onClick={(e) => e.stopPropagation()}>
                                            <video
                                                ref={(el) => { videoRefs.current[post.id] = el; }}
                                                src={post.file_url}
                                                poster={optimizedCover(post.cover_image_url)}
                                                className="w-full max-h-[90vh] object-contain"
                                                preload="metadata"
                                                playsInline
                                                onEnded={() => setPlayingVideoId(null)}
                                                onContextMenu={(e) => e.preventDefault()}
                                            />
                                            <div
                                                className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors duration-200 cursor-pointer"
                                                onClick={(e) => handleVideoPlayPause(e, post.id)}
                                            >
                                                {playingVideoId === post.id ? (
                                                    <div className="p-4 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                        <Pause className="w-8 h-8 text-white" />
                                                    </div>
                                                ) : (
                                                    <div className="p-4 bg-black/50 rounded-full hover:bg-black/70 transition-colors">
                                                        <Play className="w-8 h-8 text-white ml-1" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                )}

                                {/* Audio player — full-width cover, controls below */}
                                {post.file_url && post.mediaType === 'audio' && (() => {
                                    const gated = isGated(post.access_level, isAuthenticated, hasActiveSubscription);
                                    return (
                                        <>
                                            <div className="relative w-full aspect-square overflow-hidden">
                                                {post.cover_image_url ? (
                                                    <img src={optimizedCover(post.cover_image_url)} alt={post.title} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = post.cover_image_url; }} onContextMenu={(e) => e.preventDefault()} />
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center">
                                                        <Music className="w-8 h-8 text-purple-300/60" />
                                                    </div>
                                                )}
                                                {gated && (
                                                    <>
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                                            <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(post.access_level, 'listen')}</span>
                                                        </div>
                                                        <AccessLevelBadge accessLevel={post.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                                    </>
                                                )}
                                            </div>
                                            <div className="px-5 pt-3 pb-3">
                                                <AudioPlayer
                                                    src={post.file_url}
                                                    title={post.title || post.original_filename}
                                                    hideCoverArt
                                                    uploadId={gated ? null : post.id}
                                                    locked={gated}
                                                    onLockedClick={() => setAccessModal({ isOpen: true, item: post })}
                                                />
                                            </div>
                                        </>
                                    );
                                })()}

                                {/* Post Actions */}
                                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-700/50">
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLike(post);
                                            }}
                                            className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors ${
                                                post.user_has_liked 
                                                    ? 'text-red-400 bg-red-400/10' 
                                                    : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                            }`}
                                        >
                                            <Heart className={`w-4 h-4 ${post.user_has_liked ? 'fill-current' : ''}`} />
                                            <span className="text-sm">{post.like_count || 0}</span>
                                        </button>
                                        
                                        {post.previewComments && post.previewComments.length > 0 && (
                                            <div className="flex items-center gap-2 text-gray-400">
                                                <MessageCircle className="w-4 h-4" />
                                                <span className="text-sm">{post.previewComments.length}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {post.file_url && post.mediaType !== 'post' && (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShareClick(post);
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                                                    title="Share"
                                                >
                                                    <Share2 className="w-4 h-4" />
                                                </button>
                                                
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDownloadClick(post);
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
                                                    title="Download"
                                                >
                                                    <Download className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                        
                                        <Link
                                            to={post.mediaType === 'post' 
                                                ? `/view/post/${post.author_username}/${post.id}`
                                                : `/view/${post.mediaType}/${post.username}/${post.stored_filename || post.original_filename || post.id}`
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                                            title="View Details"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </div>

                                {/* Comments Preview */}
                                {post.previewComments && post.previewComments.length > 0 && (
                                    <div className="px-5 pb-4 pt-3 border-t border-gray-700/30">
                                        <div className="space-y-2">
                                            {post.previewComments.slice(0, 2).map((comment) => (
                                                <div key={comment.id} className="text-sm">
                                                    <span className="font-medium text-gray-300">
                                                        {comment.author_display_name || comment.author_username}:
                                                    </span>
                                                    <span className="text-gray-400 ml-1">{comment.content}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, item: null })}
                mediaTitle={shareModal.item?.title || 'Content'}
                mediaType={shareModal.item?.mediaType || 'media'}
                username={username}
                mediaId={shareModal.item?.stored_filename || shareModal.item?.original_filename || shareModal.item?.id}
                artistName={artist?.displayName || artist?.username}
            />

            {/* Download Paywall */}
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, item: null })}
                mediaTitle={downloadPaywall.item?.title || 'Content'}
                mediaType={downloadPaywall.item?.mediaType || 'media'}
                uploadId={downloadPaywall.item?.id}
            />

            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'content' })}
                contentType={signupInvite.contentType}
            />

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, item: null })}
                accessLevel={accessModal.item?.access_level}
                action="listen"
                isAuthenticated={isAuthenticated}
                viewUrl={accessModal.item ? `/view/${accessModal.item.mediaType}/${accessModal.item.username}/${accessModal.item.stored_filename || accessModal.item.original_filename || accessModal.item.id}` : null}
            />
        </div>
    );
};

export default ArtistFeed;